import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, RefreshCw, FileSpreadsheet } from "lucide-react";
import { fetchWalkthroughs, fetchPlaybookRequests, fetchResourceRequests, clearToken, importFromOldSite } from "@/lib/api";
import { MarkBtb } from "@/components/BrandLogos";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const TABS = [
  { key: "walkthrough", label: "Walkthrough" },
  { key: "playbook", label: "Playbook (IT)" },
  { key: "resources", label: "Resources" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("walkthrough");
  const [showImport, setShowImport] = useState(false);
  const [oldEmail, setOldEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [oldUrl, setOldUrl] = useState("https://beyondthebox.law");
  const [importing, setImporting] = useState(false);

  const runImport = async (e) => {
    e.preventDefault();
    setImporting(true);
    try {
      const { imported } = await importFromOldSite(oldEmail, oldPassword, oldUrl);
      const found = Object.values(imported).reduce((n, c) => n + c.found, 0);
      const added = Object.values(imported).reduce((n, c) => n + c.added, 0);
      toast.success(`Found ${found} leads on the old site; ${added} new ones copied here.`);
      setOldPassword("");
      setShowImport(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [w, p, r] = await Promise.all([
        fetchWalkthroughs(),
        fetchPlaybookRequests(),
        fetchResourceRequests(),
      ]);
      setWalkthroughs(w);
      setPlaybooks(p);
      setResources(r);
    } catch {
      toast.error("Could not load requests. Please sign in again.");
      clearToken();
      navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const items = tab === "walkthrough" ? walkthroughs : tab === "playbook" ? playbooks : resources;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [
        i.name,
        i.firm,
        i.email,
        i.role,
        i.product_interest,
        i.current_systems,
        i.it_contact_email,
        i.message,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [items, query]);

  const today = useMemo(() => {
    const d = new Date().toDateString();
    return items.filter((i) => new Date(i.created_at).toDateString() === d).length;
  }, [items]);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex w-full max-w-[1360px] items-center justify-between px-6 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-3 text-foreground">
            <MarkBtb className="h-7 w-7" strokeWidth={2} />
            <div className="flex flex-col leading-none">
              <span className="font-display text-lg font-medium tracking-tight">Beyond the Box</span>
              <span className="overline mt-1 text-[0.6rem] text-muted-foreground">Owner console</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="/Beyond_the_Box_Bible.xlsx"
              download
              data-testid="admin-bible-download"
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <FileSpreadsheet strokeWidth={1.6} className="h-4 w-4" /> Bible
            </a>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <LogOut strokeWidth={1.6} className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1360px] px-6 py-12 md:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="overline text-accent">Owner console</span>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
              Incoming leads.
            </h1>
          </div>
          {/* Stat tiles */}
          <div className="grid grid-cols-4 divide-x divide-border border border-border">
            <div className="px-6 py-5">
              <div className="font-display text-3xl font-medium text-foreground" data-testid="admin-stat-walkthrough">
                {walkthroughs.length}
              </div>
              <span className="overline text-muted-foreground">Walkthrough</span>
            </div>
            <div className="px-6 py-5">
              <div className="font-display text-3xl font-medium text-foreground" data-testid="admin-stat-playbook">
                {playbooks.length}
              </div>
              <span className="overline text-muted-foreground">Playbook</span>
            </div>
            <div className="px-6 py-5">
              <div className="font-display text-3xl font-medium text-foreground" data-testid="admin-stat-resources">
                {resources.length}
              </div>
              <span className="overline text-muted-foreground">Resources</span>
            </div>
            <div className="px-6 py-5">
              <div className="font-display text-3xl font-medium text-foreground" data-testid="admin-stat-today">
                {today}
              </div>
              <span className="overline text-muted-foreground">Today</span>
            </div>
          </div>
        </div>

        {/* One-off import from the old site */}
        <div className="mt-8 border border-border" data-testid="admin-import">
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="w-full px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground"
          >
            {showImport ? "Hide" : "Copy leads from the old site"}
          </button>
          {showImport && (
            <form onSubmit={runImport} className="grid gap-3 border-t border-border p-5 md:grid-cols-4">
              <input
                type="url"
                value={oldUrl}
                onChange={(e) => setOldUrl(e.target.value)}
                placeholder="Old site address"
                className="border border-border bg-background px-3 py-2 text-sm text-foreground"
                required
              />
              <input
                type="email"
                value={oldEmail}
                onChange={(e) => setOldEmail(e.target.value)}
                placeholder="Old site admin email"
                className="border border-border bg-background px-3 py-2 text-sm text-foreground"
                required
              />
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Old site admin password"
                className="border border-border bg-background px-3 py-2 text-sm text-foreground"
                required
              />
              <button
                type="submit"
                disabled={importing}
                className="bg-foreground px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-background disabled:opacity-50"
              >
                {importing ? "Copying..." : "Copy leads"}
              </button>
            </form>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-0 border-b border-border" data-testid="admin-tabs">
          {TABS.map((t) => {
            const count =
              t.key === "walkthrough"
                ? walkthroughs.length
                : t.key === "playbook"
                ? playbooks.length
                : resources.length;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`admin-tab-${t.key}`}
                onClick={() => {
                  setTab(t.key);
                  setQuery("");
                }}
                className={`-mb-px flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                  active
                    ? "border-accent text-accent"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
                <span className="border border-border px-2 py-0.5 text-[0.65rem] text-foreground/70">
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div className="mt-6 flex items-center gap-3 border border-border">
          <div className="flex flex-1 items-center gap-3 px-4">
            <Search strokeWidth={1.6} className="h-4 w-4 text-muted-foreground" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by firm, name, email…"
              className="w-full bg-transparent py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
          </div>
          <button
            type="button"
            data-testid="admin-refresh"
            onClick={load}
            className="flex items-center gap-2 border-l border-border px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:text-accent"
          >
            <RefreshCw strokeWidth={1.6} className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* List */}
        <div className="mt-8 border-t border-border" data-testid="admin-requests-list">
          {loading ? (
            <div className="py-20 text-center">
              <span className="overline text-muted-foreground">Loading…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="admin-empty">
              <span className="overline text-muted-foreground">
                {items.length === 0 ? "No requests yet" : "No matches"}
              </span>
            </div>
          ) : tab === "walkthrough" ? (
            filtered.map((it) => (
              <article
                key={it.id}
                data-testid="admin-request-item"
                className="grid grid-cols-1 gap-4 border-b border-border py-6 md:grid-cols-12"
              >
                <div className="md:col-span-4">
                  <div className="font-display text-2xl font-medium leading-tight tracking-tight text-foreground">
                    {it.firm}
                  </div>
                  <div className="mt-1 text-sm text-foreground/90">
                    {it.name}
                    {it.role ? <span className="text-muted-foreground"> · {it.role}</span> : null}
                  </div>
                  <a
                    href={`mailto:${it.email}`}
                    className="mt-1 inline-block text-sm text-accent underline-offset-4 hover:underline"
                  >
                    {it.email}
                  </a>
                </div>

                <div className="md:col-span-5">
                  <div className="flex flex-wrap gap-2">
                    {it.product_interest && (
                      <span className="border border-border px-3 py-1 text-xs tracking-wide text-foreground/80">
                        {it.product_interest}
                      </span>
                    )}
                    {it.firm_size && (
                      <span className="border border-border px-3 py-1 text-xs tracking-wide text-foreground/80">
                        {it.firm_size}
                      </span>
                    )}
                    {it.current_systems && (
                      <span className="border border-border px-3 py-1 text-xs tracking-wide text-muted-foreground">
                        Current: {it.current_systems}
                      </span>
                    )}
                  </div>
                  {it.message && (
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                      “{it.message}”
                    </p>
                  )}
                </div>

                <div className="md:col-span-3 md:text-right">
                  <span className="overline text-muted-foreground">{fmtDate(it.created_at)}</span>
                </div>
              </article>
            ))
          ) : tab === "playbook" ? (
            filtered.map((it) => (
              <article
                key={it.id}
                data-testid="admin-playbook-item"
                className="grid grid-cols-1 gap-4 border-b border-border py-6 md:grid-cols-12"
              >
                <div className="md:col-span-4">
                  <div className="font-display text-2xl font-medium leading-tight tracking-tight text-foreground">
                    {it.firm}
                  </div>
                  <div className="mt-1 text-sm text-foreground/90">{it.name}</div>
                  <a
                    href={`mailto:${it.email}`}
                    className="mt-1 inline-block text-sm text-accent underline-offset-4 hover:underline"
                  >
                    {it.email}
                  </a>
                </div>

                <div className="md:col-span-5">
                  {it.it_contact_email && (
                    <div className="flex flex-wrap gap-2">
                      <span className="border border-border px-3 py-1 text-xs tracking-wide text-muted-foreground">
                        IT contact:{" "}
                        <a
                          href={`mailto:${it.it_contact_email}`}
                          className="text-accent underline-offset-4 hover:underline"
                        >
                          {it.it_contact_email}
                        </a>
                      </span>
                    </div>
                  )}
                  {it.message && (
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                      “{it.message}”
                    </p>
                  )}
                </div>

                <div className="md:col-span-3 md:text-right">
                  <span className="overline text-muted-foreground">{fmtDate(it.created_at)}</span>
                </div>
              </article>
            ))
          ) : (
            filtered.map((it) => (
              <article
                key={it.id}
                data-testid="admin-resource-item"
                className="grid grid-cols-1 gap-4 border-b border-border py-6 md:grid-cols-12"
              >
                <div className="md:col-span-4">
                  <div className="font-display text-2xl font-medium leading-tight tracking-tight text-foreground">
                    {it.firm}
                  </div>
                  <div className="mt-1 text-sm text-foreground/90">{it.name}</div>
                  <a
                    href={`mailto:${it.email}`}
                    className="mt-1 inline-block text-sm text-accent underline-offset-4 hover:underline"
                  >
                    {it.email}
                  </a>
                </div>
                <div className="md:col-span-5">
                  <span className="border border-border px-3 py-1 text-xs tracking-wide text-foreground/80">
                    Downloaded: {it.resource}
                  </span>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <span className="overline text-muted-foreground">{fmtDate(it.created_at)}</span>
                </div>
              </article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
